import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { PageHeader, PageBody, StatusPill } from "@/components/PageChrome";
import { T } from "@/lib/testIds";
import PaginationBar from "@/components/PaginationBar";
import useClientPagination from "@/hooks/useClientPagination";

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const pag = useClientPagination(rows, "customers");

  const load = async () => {
    try {
      const { data } = await api.get("/customers", { params: q ? { q } : {} });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <>
      <PageHeader title="Customers" subtitle="Unique customers keyed by phone number" testId={T.moduleTitle("customers")} module="customers">
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid={T.searchInput("customers")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name/phone/city…"
            className="h-8 pl-7 pr-2 w-64 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </PageHeader>
      <PageBody>
        <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Name</th><th>Phone</th><th>City / State</th><th>Orders</th><th>Spend ₹</th>
                <th>Marketplaces</th><th>Return Accept %</th><th>RTO %</th><th>Refund %</th><th>Last Order</th>
              </tr>
            </thead>
            <tbody>
              {pag.pageRows.map((c) => (
                <tr key={c.phone} data-testid={T.row("customers", c.phone)} onClick={() => navigate(`/customers/${encodeURIComponent(c.phone)}`)} className="cursor-pointer">
                  <td className="font-medium">{c.name || "—"}</td>
                  <td className="font-mono">{c.phone}</td>
                  <td className="text-slate-600">{[c.city, c.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="font-mono">{c.total_orders}</td>
                  <td className="font-mono">₹{c.total_spend.toLocaleString()}</td>
                  <td>{c.marketplaces.map((m) => <StatusPill key={m} value={m} tone="info" />)}</td>
                  <td className="font-mono">{c.returns_initiated ? `${c.return_acceptance_rate}%` : "—"}</td>
                  <td className="font-mono">{c.rto_rate}%</td>
                  <td className="font-mono">{c.refund_rate}%</td>
                  <td className="font-mono">{c.last_purchase_date || "—"}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={10} className="text-center py-8 text-slate-500">No customers yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-2 flex justify-end">
          <PaginationBar page={pag.page} pageSize={pag.pageSize} total={pag.total}
            onPageChange={pag.setPage} onPageSizeChange={pag.setPageSize} testIdPrefix="customers" />
        </div>
      </PageBody>
    </>
  );
}
